import * as React from "react";
import Spinner from "~/common/components/Spinner/Spinner";
import { NoSearchResults } from "~/features/search/components/common/NoSearchResults";
import { HSAResultsTable } from "~/features/selectionAssistant/housing/components/HousingTabularView/HSAResultsTable";
import { useSelector } from "react-redux";
import {
  selectIsInFlight,
  selectLastSearchReason,
  selectTotalNumberOfResults,
} from "~/features/selectionAssistant/housing/redux/selectors";
import { useFormContext, useWatch } from "react-hook-form";
import { type HousingSearchForm } from "~/features/selectionAssistant/housing/types/housingSAFormTypes";
import { type RootState } from "~/app/store";
import { Button } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { SearchReason } from "~/features/selectionAssistant/housing/redux/types";

interface Props {
  submitHandler: (e?: React.BaseSyntheticEvent) => unknown;
}

export const HSASearchResults = React.memo(({ submitHandler }: Props) => {
  const { t } = useTranslation();

  const isInFlight = useSelector(selectIsInFlight);
  const lastSearchReason = useSelector(selectLastSearchReason);
  const totalNumberOfResults = useSelector(selectTotalNumberOfResults);

  const pageAmount = useSelector(selectPageAmount);
  const { setValue, control } = useFormContext<HousingSearchForm>();
  const currentPage = useWatch({ control, name: "page" });
  const onPageChanged = (newPageNumber: number) => {
    setValue("page", newPageNumber);
    submitHandler();
  };

  const showResults = !isInFlight || lastSearchReason === SearchReason.PAGINATION;
  const showLoadMore = pageAmount > 1 && currentPage + 1 < pageAmount;

  return (
    <div id="results" className={"scha__sa__results"}>
      {isInFlight && <Spinner />}

      {showResults && totalNumberOfResults === 0 && <NoSearchResults mode={"short"} variant="filter" />}
      {showResults && totalNumberOfResults > 0 && (
        <>
          <HSAResultsTable totalNumberOfResults={totalNumberOfResults} submitHandler={submitHandler} />
          {showLoadMore && (
            <div className={"scha__sa__pagination"}>
              <Button loading={isInFlight} onClick={() => onPageChanged(currentPage + 1)}>
                {t("page.hsa.showMore")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
});

const selectPageAmount = (state: RootState) => state.housingSA.searchResults.pageAmount;
